/**
 * GET /api/initiatives/list (and /api/admin/initiatives) — list of initiatives.
 *
 * Backend returns rows with field name `id` (verified at
 * backend/src/handlers/tracker.ts:190,409 — initiatives are written with
 * `{ id: initId, opcoId, operatingGroup, name, ... }`).
 *
 * Used by useInitiatives + useAdminInitiatives. Replaces `data.initiatives ?? []`
 * defensive coercion at the hook layer.
 */
import { z } from 'zod';

export const InitiativeSchema = z.looseObject({
  id: z.string().min(1),
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  opcoId: z.string().nullable().optional(),
  opcoName: z.string().nullable().optional(),
  operatingGroup: z.string().nullable().optional(),
  operatingPartner: z.string().nullable().optional(),
  functionalArea: z.string().nullable().optional(),
  stage: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  health: z.string().nullable().optional(),
  // techStack is a single free-text field everywhere in the UI (text input,
  // inline card render, CSV export). Some rows — notably ones created via
  // Tendril — stored it as an array of strings. Accept either and normalize
  // arrays to a comma-joined string at the boundary so the whole list response
  // isn't rejected and every downstream consumer keeps receiving a string.
  techStack: z
    .union([z.string(), z.array(z.string())])
    .nullable()
    .optional()
    .transform((v) => (Array.isArray(v) ? v.join(', ') : v)),
  lifecyclePhase: z.string().nullable().optional(),
  // Some legacy rows store arrImpactType as a string ("organic_new_features");
  // newer rows use an array of strings (e.g. ["organic_new_features",
  // "internal_efficiency"]) for the multi-select impact-type UX. Accept either
  // to keep the boundary tolerant; consumers normalize via Array.isArray() at
  // render time.
  arrImpactType: z
    .union([z.string(), z.array(z.string())])
    .nullable()
    .optional(),
  startDate: z.string().nullable().optional(),
  liveDate: z.string().nullable().optional(),
  targetDate: z.string().nullable().optional(),
  completedAt: z.string().nullable().optional(),
  arrImpact2026: z.number().nullable().optional(),
  arrImpact2027: z.number().nullable().optional(),
  arrImpact2028: z.number().nullable().optional(),
  comments: z.string().nullable().optional(),
  // Portfolio-group QBR customizations (Phase 1): roadmap horizon category
  // and AI-authored-content opt-out flag. Both default when absent so
  // pre-Phase-1 rows (and any row that omits them) still validate.
  roadmapCategory: z
    .enum(['stabilize_scale', 'grow_expand', 'transformational_bet', 'not_set'])
    .nullable()
    .optional()
    .default('not_set'),
  aiEnabled: z.boolean().nullable().optional().default(true),
  createdBy: z.string().nullable().optional(),
  createdByEmail: z.string().nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type Initiative = z.infer<typeof InitiativeSchema>;

/** GET /api/initiatives/list (or /api/admin/initiatives) — `{ initiatives: Initiative[] }`. */
export const InitiativesListResponseSchema = z.looseObject({
  initiatives: z.array(InitiativeSchema),
});

export type InitiativesListResponse = z.infer<typeof InitiativesListResponseSchema>;
